import React from 'react';
import { Drawer, Box, Typography, Card, CardContent, Divider, Link, List, ListItem, ListItemIcon, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import InfoIcon from '@mui/icons-material/Info';
import SupportIcon from '@mui/icons-material/Support';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const StyledLink = styled(Link)(() => ({
  color: '#925d49',
  textDecoration: 'underline',
  '&:hover': {
    color: '#402d20',
  },
}));

const SmallFiberManualRecordIcon = styled(FiberManualRecordIcon)(({ size }) => ({
  fontSize: size || '8px',
}));

const LeftNav = ({ isCollapsed, setIsCollapsed, mobileOpen, setMobileOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleCollapse = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerClose}
      sx={{
        width: isMobile ? '100%' : (isCollapsed ? 60 : 360),
        flexShrink: 0,
        transition: 'width 0.3s ease',
        [`& .MuiDrawer-paper`]: {
          width: isMobile ? '100%' : (isCollapsed ? 60 : 360),
          maxWidth: isMobile ? '320px' : 'none',
          boxSizing: 'border-box',
          backgroundColor: '#edeeee',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ padding: isCollapsed ? 1 : 3, position: 'relative' }}>
        {/* Toggle Button */}
        <IconButton
          onClick={toggleCollapse}
          sx={{
            position: 'absolute',
            top: 8,
            right: isMobile ? 8 : (isCollapsed ? 8 : 8),
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
            width: isMobile ? 40 : 32,
            height: isMobile ? 40 : 32,
            minHeight: 44,
            minWidth: 44,
          }}
          size="small"
          aria-label={isMobile ? 'Close navigation menu' : (isCollapsed ? 'Expand navigation' : 'Collapse navigation')}
        >
          {isMobile ? <ChevronLeftIcon /> : (isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
        </IconButton>

        {/* Header - Hidden when collapsed on desktop, always shown on mobile */}
        {(!isCollapsed || isMobile) && (
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ marginTop: 4 }}>
            About this Solution
          </Typography>
        )}

        {/* How It Works Card */}
        {(!isCollapsed || isMobile) && (
          <Card
            sx={{
              marginBottom: 3,
              borderRadius: 2,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <InfoIcon fontSize="large" color="primary" />
                <Typography variant="h6" fontWeight="bold" sx={{ marginLeft: 1 }}>
                  How It Works
                </Typography>
              </Box>
              <Divider sx={{ marginBottom: 2 }} />
              <List disablePadding>
                {[
                  <>Sign in with your Marshall username and password and select a PDF to upload for remediation.</>,
                  <>The tool uses an automated approach, supported by <strong>Adobe's API</strong> and <strong>AWS services</strong>, to fix common accessibility issues like missing tags, incorrect reading order, Alt Text and more.</>,
                  <>Within a short time, you'll receive your remediated PDF and accessibility reports ready to download.</>,
                ].map((item, i) => (
                  <ListItem
                    key={i}
                    disablePadding
                    alignItems="flex-start"
                    sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}
                  >
                    <ListItemIcon sx={{ minWidth: 24, mt: '4px' }}>
                      <Typography variant="body2" fontWeight="bold">{i + 1}.</Typography>
                    </ListItemIcon>
                    <Typography variant="body2">{item}</Typography>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Document Requirements Card - Hidden when collapsed on desktop, always shown on mobile */}
        {(!isCollapsed || isMobile) && (
          <Card
            sx={{
              marginBottom: 3,
              borderRadius: 2,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
          <CardContent>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <DocumentScannerIcon fontSize="large" color="primary" />
              <Typography variant="h6" fontWeight="bold" sx={{ marginLeft: 1 }}>
                Document Requirements
              </Typography>
            </Box>
            <Divider sx={{ marginBottom: 2 }} />
            <List disablePadding>
              {[
                <>Each user is limited to <strong>100 PDF document uploads</strong>.</>,
                <>Documents cannot exceed <strong>100 pages</strong>.</>,
                <>Documents must be smaller than <strong>50 MB</strong>.</>,
                <>Do not upload documents containing <strong>sensitive information</strong>.</>,
                <>This solution only remediates <strong>PDF documents</strong>. Other document types will not be accepted.</>,
                <>This solution does not remediate <strong>fillable forms</strong> or handle <strong>color selection/contrast</strong>.</>,
              ].map((item, i) => (
                <ListItem
                  key={i}
                  disablePadding
                  alignItems="flex-start"
                  sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}
                >
                  <ListItemIcon sx={{ minWidth: 24, mt: '4px' }}>
                    <Typography variant="body2" fontWeight="bold">{i + 1}.</Typography>
                  </ListItemIcon>
                  <Typography variant="body2">{item}</Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
        )}

        {/* Support Resources Card - Hidden when collapsed on desktop, always shown on mobile */}
        {(!isCollapsed || isMobile) && (
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
          <CardContent>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <SupportIcon fontSize="large" color="secondary" />
              <Typography variant="h6" fontWeight="bold" sx={{ marginLeft: 1 }}>
                Support Resources
              </Typography>
            </Box>
            <Divider sx={{ marginBottom: 2 }} />
            <List disablePadding>
              {[
                {
                  href: 'https://mymu.marshall.edu/s/article/Overview-PDF-Remediation-for-WGAC-Compliance',
                  label: 'Overview: PDF Remediation for WGAC Compliance',
                },
                {
                  href: 'https://www.marshall.edu/design-center/digital-accessibility-resource-center/',
                  label: 'Digital Accessibility Resource Center - Design Center',
                },
                {
                  href: 'https://www.marshall.edu/policies/files/2026/03/UPGA-14-Digital-Accessibility-Policy.pdf',
                  label: 'UPGA-14-Digital-Accessibility-Policy.pdf',
                },
                {
                  href: 'https://www.marshall.edu/accessibility/',
                  label: 'Office of Accessibility and Accommodations',
                },
				{
					href: 'https://outlook.office.com/book/AcademicTechnologyTeam1@livemarshall.onmicrosoft.com/?ismsaljsauthenabled=true',
					label: 'MUIT Education Team',
				},
              ].map(({ href, label }) => (
                <ListItem
                  key={href}
                  disablePadding
                  alignItems="flex-start"
                  sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}
                >
                  <ListItemIcon sx={{ minWidth: 20, mt: '8px' }}>
                    <SmallFiberManualRecordIcon size="8px" sx={{ color: '#000000' }} />
                  </ListItemIcon>
                  <StyledLink href={href} target="_blank" rel="noopener">
                    {label}
                  </StyledLink>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
        )}
      </Box>
    </Drawer>
  );
};

export default LeftNav;
